import React, { useContext } from 'react';
import { ThemeContext, DEFAULT_THEME, getShapeClasses } from '../context/ThemeContext.jsx';
import { apiFetch } from '../api/index.js';
import { DynamicIcon } from '../components/icons.jsx';
import { Button } from '../components/Common.jsx';
import { useNotification } from '../context/NotificationContext.jsx';

const ColorPalette = ({ title, colors, selectedColor, onColorSelect }) => (
    <div>
        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">{title}</h4>
        <div className="flex flex-wrap gap-3">
            {colors.map((color) => (
                <button
                    key={color.value}
                    title={color.name}
                    onClick={() => onColorSelect(color.value)}
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110 border-2 dark:border-gray-700"
                    style={{ backgroundColor: color.value }}
                    aria-label={color.name}
                >
                    {selectedColor?.toLowerCase() === color.value.toLowerCase() && <DynamicIcon name="Check" className="w-5 h-5" style={{ color: ['#ffffff', '#f2f2f2', '#f8cbad'].includes(color.value.toLowerCase()) ? '#000000' : '#ffffff' }} />}
                </button>
            ))}
        </div>
    </div>
);


const SettingsCard = ({ iconName, title, children }) => (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
            <DynamicIcon name={iconName} className="w-6 h-6 mr-3 text-primary" />
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{title}</h3>
        </div>
        {children}
    </div>
);

const AppearanceSettings = ({ token }) => {
    const { theme, setTheme } = useContext(ThemeContext);
    const { addNotification } = useNotification();

    const handleSaveSettings = async () => {
        try {
            await apiFetch('/users/me/theme', token, {
                method: 'PUT',
                body: JSON.stringify(theme),
            });
            addNotification('Appearance settings saved successfully!');
        } catch (error) {
            console.error("Failed to save theme settings:", error);
            addNotification('Error: Could not save theme settings.', 'error');
        }
    };

    const handleLoadDefaults = () => {
        setTheme(DEFAULT_THEME);
        addNotification('Default settings have been loaded.');
    };

    const accentColors = [{ name: 'Aidas Blue', value: '#0068B3' }, { name: 'Aidas Navy', value: '#003466' }, { name: 'Aidas Orange', value: '#FE9900' }, { name: 'Green', value: '#22c55e' }, { name: 'Purple', value: '#a855f7' }, { name: 'Rose', value: '#f43f5e' }, { name: 'Light Gray', value: '#e5e7eb' }, { name: 'Gray', value: '#d1d5db' }, { name: 'White', value: '#FFFFFF' }];
    const backgroundColors = [{ name: 'Aidas Blue', value: '#0068B3' }, { name: 'Aidas Navy', value: '#003466' }, { name: 'Aidas Orange', value: '#FE9900' }, { name: 'Green', value: '#22c55e' }, { name: 'Purple', value: '#a855f7' }, { name: 'Rose', value: '#f43f5e' }, { name: 'Light Gray', value: '#e5e7eb' }, { name: 'Gray', value: '#d1d5db' }, { name: 'White', value: '#FFFFFF' }];
    const textColors = [{ name: 'Aidas Blue', value: '#0068B3' }, { name: 'Aidas Navy', value: '#003466' }, { name: 'Aidas Orange', value: '#FE9900' }, { name: 'Green', value: '#22c55e' }, { name: 'Purple', value: '#a855f7' }, { name: 'Rose', value: '#f43f5e' }, { name: 'Light Gray', value: '#e5e7eb' }, { name: 'Gray', value: '#d1d5db' }, { name: 'White', value: '#FFFFFF' }];

    const fonts = ['Inter', 'Poppins', 'Lora', 'Roboto Slab', 'Inconsolata', 'JetBrains Mono'];
    const shapes = [{ name: 'Rounded', value: 'rounded' }, { name: 'Pill', value: 'pill' }, { name: 'Square', value: 'square' }];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SettingsCard iconName="Sun" title="Mode">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">Choose your preferred interface look.</p>
                    <div className="flex gap-4">
                        <button onClick={() => setTheme(prev => ({ ...prev, mode: 'light' }))} className={`w-full p-4 rounded-lg border-2 transition-colors ${theme.mode === 'light' ? 'border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                            <DynamicIcon name="Sun" className="mx-auto mb-2 w-8 h-8" />
                            <span className="font-semibold">Light</span>
                        </button>
                        <button onClick={() => setTheme(prev => ({ ...prev, mode: 'dark' }))} className={`w-full p-4 rounded-lg border-2 transition-colors ${theme.mode === 'dark' ? 'border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                            <DynamicIcon name="Moon" className="mx-auto mb-2 w-8 h-8" />
                            <span className="font-semibold">Dark</span>
                        </button>
                    </div>
                </SettingsCard>

                <SettingsCard iconName="Palette" title="Accent Color">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">Select the primary color for the UI.</p>
                    <ColorPalette colors={accentColors} selectedColor={theme.primaryColor} onColorSelect={color => setTheme(t => ({ ...t, primaryColor: color }))} />
                </SettingsCard>

                <SettingsCard iconName="Type" title="Typography">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">Change the global font family.</p>
                    <select
                        value={theme.font}
                        onChange={(e) => setTheme(prev => ({ ...prev, font: e.target.value }))}
                        className={`w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-primary focus:border-primary`}
                    >
                        {fonts.map(font => (
                            <option key={font} value={font}>{font}</option>
                        ))}
                    </select>
                </SettingsCard>

                <SettingsCard iconName="Component" title="UI Shape">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">Define the border radius for controls.</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        {shapes.map(s => (
                            <button
                                key={s.name}
                                onClick={() => setTheme(prev => ({ ...prev, shape: s.value }))}
                                className={`w-full p-4 rounded-lg border-2 flex flex-col items-center justify-center transition-colors ${theme.shape === s.value ? 'border-primary' : 'border-gray-300 dark:border-gray-600'}`}
                            >
                                <div
                                    className={`w-16 h-8 mb-2 transition-all ${getShapeClasses(s.value)}`}
                                    style={{ backgroundColor: theme.primaryColor }}
                                ></div>
                                <span className="font-semibold">{s.name}</span>
                            </button>
                        ))}
                    </div>
                </SettingsCard>

                <SettingsCard iconName="Briefcase" title="Sidebar Settings">
                    <div className="space-y-4">
                        <ColorPalette title="Background Color" colors={backgroundColors} selectedColor={theme.sidebarTheme.bg} onColorSelect={color => setTheme(t => ({ ...t, sidebarTheme: { ...t.sidebarTheme, bg: color } }))} />
                        <ColorPalette title="Text Color" colors={textColors} selectedColor={theme.sidebarTheme.text} onColorSelect={color => setTheme(t => ({ ...t, sidebarTheme: { ...t.sidebarTheme, text: color } }))} />
                    </div>
                </SettingsCard>

                <SettingsCard iconName="Menu" title="Header Settings">
                    <div className="space-y-4">
                        <ColorPalette title="Background Color" colors={backgroundColors} selectedColor={theme.headerTheme.bg} onColorSelect={color => setTheme(t => ({ ...t, headerTheme: { ...t.headerTheme, bg: color } }))} />
                    </div>
                </SettingsCard>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">Manage Preferences</h3>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Save your settings or restore to default.</p>
                </div>
                <div className="flex items-center gap-4">
                    <Button onClick={handleLoadDefaults} variant="secondary"><DynamicIcon name="RotateCcw" className="w-5 h-5 mr-2" />Load Defaults</Button>
                    <Button onClick={handleSaveSettings}><DynamicIcon name="Save" className="w-5 h-5 mr-2" />Save Settings</Button>
                </div>
            </div>
        </div>
    );
};

export default AppearanceSettings;
